import { useMemo, useRef, useState, type PointerEvent } from "react";

import { Eye, Maximize2, Plus, Star, Undo2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Field } from "@/components/cinch/Field";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { useCan } from "@/lib/auth/useCan";

import {
  useCreateNote,
  usePositionNote,
  useRescaleGrid,
  useRevealAll,
  useRevertRescale,
  type Note,
  type Workspace,
} from "../api";
import {
  BOARD_META,
  IMPACT_CATEGORIES,
  impactCategoryMeta,
  scoreToPercent,
  type Board,
  type StickyTone,
} from "../constants";

/** Collapsed grid markers: light tinted chip (per the DS chip rule) with a
 *  solid dot in the impact-category color on the anchored end. */
const DOT_TONES: Record<StickyTone, string> = {
  green: "bg-success",
  blue: "bg-info",
  orange: "bg-orange",
  gray: "bg-neutral",
  yellow: "bg-warning",
  red: "bg-danger",
};

const CHIP_TONES: Record<StickyTone, string> = {
  green: "bg-success-light border-success/40",
  blue: "bg-info-light border-info/40",
  orange: "bg-orange-light border-orange/40",
  gray: "bg-neutral-light border-neutral/40",
  yellow: "bg-warning-light border-warning/40",
  red: "bg-danger-light border-danger/40",
};
import { GridNoteDialog } from "./GridNoteDialog";
import { NoteCard } from "./NoteCard";

/** "from Strengths · Market Position" — the trail back to the SWOT theme an
 *  initiative was promoted from. */
function LineageLink({
  note,
  workspace,
  onNavigateBoard,
}: {
  note: Note;
  workspace: Workspace;
  onNavigateBoard: (board: Board) => void;
}) {
  const lane = note.source_category_id
    ? workspace.categories.find((c) => c.id === note.source_category_id)
    : undefined;
  if (!lane || lane.board === "impact_effort") return null;
  return (
    <button
      type="button"
      className="mt-1 block max-w-full truncate text-left text-[10px] font-medium text-brand hover:underline"
      onPointerDown={(e) => e.stopPropagation()}
      onClick={(e) => {
        e.stopPropagation();
        onNavigateBoard(lane.board);
      }}
    >
      from {BOARD_META[lane.board].label} · {lane.name}
    </button>
  );
}

/**
 * The Impact vs Effort wall. Notes with revealed votes place themselves by the
 * room's averages (score 5,5 = the axis cross); the facilitator can drag any
 * note to override (its top-left corner is the anchor, like the physical
 * wall). Unscored/unrevealed notes wait in the tray on the left.
 */
export function ImpactEffortBoard({
  workspace,
  onNavigateBoard,
  highlight = null,
}: {
  workspace: Workspace;
  onNavigateBoard: (board: Board) => void;
  /** `data-planning-hl` key of a search-result target to shake/halo. */
  highlight?: string | null;
}) {
  const sessionId = workspace.session.id;
  const isAdmin = useCan("planning.admin");
  const position = usePositionNote(sessionId);
  const revealAll = useRevealAll(sessionId);

  const rescale = useRescaleGrid(sessionId);
  const revertRescale = useRevertRescale(sessionId);

  const [detailId, setDetailId] = useState<string | null>(null);
  // The one note showing as a full card on the canvas; everything else
  // renders as a dot + title so a crowded grid stays readable.
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [composerOpen, setComposerOpen] = useState(false);
  const [rescaleOpen, setRescaleOpen] = useState(false);
  // Local drag ghost: {id, x, y} in percent while a pointer drag is active,
  // so the 2s poll never fights the hand that's dragging.
  const [drag, setDrag] = useState<{ id: string; x: number; y: number } | null>(null);
  const dragMoved = useRef(false);
  // Pointer-down bookkeeping. Nothing moves on press: the ghost only engages
  // past a small travel threshold (real mice jitter a pixel or two during a
  // click), and it moves relative to where the note was GRABBED — not by
  // snapping the note's anchor to the pointer.
  const dragOrigin = useRef<{ x: number; y: number } | null>(null); // client px
  const dragOffset = useRef<{ dx: number; dy: number }>({ dx: 0, dy: 0 }); // pct
  const pendingDragId = useRef<string | null>(null);
  const canvasRef = useRef<HTMLDivElement>(null);

  const notes = useMemo(
    () =>
      workspace.notes.filter((n) => n.board === "impact_effort" && n.status === "posted"),
    [workspace.notes],
  );

  const placedPos = (n: Note): { x: number; y: number } | null => {
    if (drag?.id === n.id) return { x: drag.x, y: drag.y };
    if (n.placement_override && n.pos_x !== null && n.pos_y !== null)
      return { x: n.pos_x, y: n.pos_y };
    const vs = n.vote_summary;
    if (vs.revealed && vs.avg_effort !== null && vs.avg_impact !== null)
      return scoreToPercent(vs.avg_effort, vs.avg_impact);
    return null;
  };

  const placed = notes.filter((n) => placedPos(n) !== null);
  const tray = notes.filter((n) => placedPos(n) === null);
  const unrevealed = notes.filter((n) => !n.votes_revealed);

  const pctFromEvent = (e: PointerEvent): { x: number; y: number } | null => {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return null;
    return {
      x: Math.min(100, Math.max(0, ((e.clientX - rect.left) / rect.width) * 100)),
      y: Math.min(100, Math.max(0, ((e.clientY - rect.top) / rect.height) * 100)),
    };
  };

  const startDrag = (note: Note) => (e: PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    dragMoved.current = false;
    pendingDragId.current = note.id;
    dragOrigin.current = { x: e.clientX, y: e.clientY };
    // Grab offset: how far the pointer is from the note's anchor, so dragging
    // moves the note from where it sits — the anchor never snaps to the hand.
    const pointer = pctFromEvent(e);
    const pos = placedPos(note);
    dragOffset.current =
      pointer && pos ? { dx: pointer.x - pos.x, dy: pointer.y - pos.y } : { dx: 0, dy: 0 };
    // Deliberately NO setDrag here: a mere press must not move anything.
  };

  const moveDrag = (e: PointerEvent<HTMLDivElement>) => {
    const id = pendingDragId.current;
    if (!id) return;
    // A click's natural jitter isn't a drag: nothing moves (and the tap isn't
    // forfeited) until the pointer has clearly traveled.
    if (!dragMoved.current) {
      const o = dragOrigin.current;
      if (!o || Math.hypot(e.clientX - o.x, e.clientY - o.y) < 6) return;
      dragMoved.current = true;
    }
    const p = pctFromEvent(e);
    if (!p) return;
    const { dx, dy } = dragOffset.current;
    setDrag({
      id,
      x: Math.min(100, Math.max(0, p.x - dx)),
      y: Math.min(100, Math.max(0, p.y - dy)),
    });
  };

  const endDrag = (note: Note, onTap: () => void) => (e: PointerEvent<HTMLDivElement>) => {
    e.currentTarget.releasePointerCapture(e.pointerId);
    if (pendingDragId.current !== note.id) return;
    pendingDragId.current = null;
    if (!dragMoved.current) {
      // A clean click, not a drag.
      onTap();
      return;
    }
    const finalPos = drag && drag.id === note.id ? { x: drag.x, y: drag.y } : null;
    setDrag(null);
    if (finalPos) {
      position.mutate(
        { id: note.id, ...finalPos },
        { onError: (err) => toast.error(err.message) },
      );
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
          {IMPACT_CATEGORIES.map((c) => (
            <span key={c.value} className="inline-flex items-center gap-1.5">
              <span
                className={cn("h-2.5 w-2.5 rounded-sm border", {
                  "border-success/40 bg-success-light": c.tone === "green",
                  "border-info/40 bg-info-light": c.tone === "blue",
                  "border-orange/40 bg-orange-light": c.tone === "orange",
                  "border-neutral/40 bg-neutral-light": c.tone === "gray",
                })}
              />
              {c.label}
            </span>
          ))}
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => setComposerOpen(true)}>
            <Plus className="mr-1 h-3.5 w-3.5" /> Initiative
          </Button>
          {isAdmin && placed.length >= 2 ? (
            <Button
              size="sm"
              variant="outline"
              className="h-8 text-xs"
              disabled={rescale.isPending}
              onClick={() => setRescaleOpen(true)}
            >
              <Maximize2 className="mr-1 h-3.5 w-3.5" /> Rescale
            </Button>
          ) : null}
          {isAdmin && workspace.session.can_revert_rescale ? (
            <Button
              size="sm"
              variant="outline"
              className="h-8 text-xs"
              disabled={revertRescale.isPending}
              onClick={() =>
                revertRescale.mutate(undefined, {
                  onSuccess: () => toast.success("Scale reverted to pre-rescale positions"),
                  onError: (err) => toast.error(err.message),
                })
              }
            >
              <Undo2 className="mr-1 h-3.5 w-3.5" /> Revert scale
            </Button>
          ) : null}
          {isAdmin && unrevealed.length > 0 ? (
            <Button
              size="sm"
              className="h-8 text-xs"
              onClick={() =>
                revealAll.mutate(undefined, {
                  onSuccess: () => toast.success("All votes revealed"),
                  onError: (err) => toast.error(err.message),
                })
              }
            >
              <Eye className="mr-1 h-3.5 w-3.5" /> Reveal all votes
            </Button>
          ) : null}
        </div>
      </div>

      <div className="flex gap-4">
        {/* ── Tray: not yet scored/revealed ── */}
        <aside className="w-56 shrink-0 space-y-2">
          <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            To score ({tray.length})
          </div>
          {tray.map((n) => (
            <div key={n.id} className="space-y-0.5">
              <NoteCard
                note={n}
                size="sm"
                data-planning-hl={`note-${n.id}`}
                className={cn(
                  "w-full cursor-pointer",
                  highlight === `note-${n.id}` && "planning-highlight",
                )}
                onClick={() => setDetailId(n.id)}
              >
                <LineageLink note={n} workspace={workspace} onNavigateBoard={onNavigateBoard} />
              </NoteCard>
              <div className="text-right text-[10px] text-muted-foreground">
                {n.vote_summary.count} vote{n.vote_summary.count === 1 ? "" : "s"} in
                {n.vote_summary.my_vote ? " · you voted" : ""}
              </div>
            </div>
          ))}
          {tray.length === 0 ? (
            <p className="text-xs text-muted-foreground">
              Everything is on the grid. Add initiatives here or promote SWOT themes.
            </p>
          ) : null}
        </aside>

        {/* ── The grid ── */}
        <div className="min-w-0 flex-1">
          <div className="flex">
            <div className="flex w-8 shrink-0 flex-col items-center justify-between py-6 text-[10px] font-medium text-muted-foreground">
              <span>High</span>
              <span className="-rotate-90 whitespace-nowrap text-xs font-semibold tracking-widest">
                Impact
              </span>
              <span>Low</span>
            </div>
            <div className="min-w-0 flex-1">
              <div
                ref={canvasRef}
                className="relative h-[640px] touch-none overflow-hidden rounded-md border bg-card"
                onPointerDown={(e) => {
                  // Clicking empty canvas collapses the expanded card.
                  if (e.target === canvasRef.current) setExpandedId(null);
                }}
              >
                {/* Faint gridlines at every whole-number tick (10%..90%);
                    the center cross (50%) draws stronger on top. */}
                {[10, 20, 30, 40, 60, 70, 80, 90].map((pct) => (
                  <div
                    key={`v${pct}`}
                    className="pointer-events-none absolute inset-y-0 w-px bg-border/40"
                    style={{ left: `${pct}%` }}
                  />
                ))}
                {[10, 20, 30, 40, 60, 70, 80, 90].map((pct) => (
                  <div
                    key={`h${pct}`}
                    className="pointer-events-none absolute inset-x-0 h-px bg-border/40"
                    style={{ top: `${pct}%` }}
                  />
                ))}
                {/* Axis cross — power blue so the center reads clearly. */}
                <div className="pointer-events-none absolute inset-y-0 left-1/2 w-px bg-power/60" />
                <div className="pointer-events-none absolute inset-x-0 top-1/2 h-px bg-power/60" />
                {/* Quadrant hints */}
                <span className="pointer-events-none absolute left-2 top-1.5 text-[10px] font-medium uppercase tracking-wider text-success/70">
                  Do these
                </span>
                <span className="pointer-events-none absolute right-2 top-1.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground/70">
                  Big bets — must-dos live here
                </span>
                <span className="pointer-events-none absolute bottom-1.5 left-2 text-[10px] font-medium uppercase tracking-wider text-muted-foreground/70">
                  Fill-ins
                </span>
                <span className="pointer-events-none absolute bottom-1.5 right-2 text-[10px] font-medium uppercase tracking-wider text-danger/50">
                  Reconsider
                </span>

                {placed.map((n) => {
                  const p = placedPos(n)!;

                  // Collapsed by default: a tinted chip — title inside, solid
                  // category dot on the anchored end, which sits exactly on
                  // the grid coordinate — so a crowded grid stays workable.
                  // Tap expands to the full card; drag works from either form.
                  if (expandedId !== n.id) {
                    const tone = impactCategoryMeta(n.impact_category)?.tone ?? "yellow";
                    const labelLeft = p.x > 55; // chip opens toward the canvas center
                    return (
                      <div
                        key={n.id}
                        className="absolute z-[5]"
                        style={{ left: `${p.x}%`, top: `${p.y}%` }}
                      >
                        <div
                          data-planning-hl={`note-${n.id}`}
                          className={cn(
                            "flex max-w-44 cursor-grab select-none items-center gap-1.5 rounded-full border px-2 py-0.5 shadow-sm active:cursor-grabbing",
                            CHIP_TONES[tone],
                            labelLeft && "flex-row-reverse",
                            drag?.id === n.id && "z-10 shadow-md",
                            highlight === `note-${n.id}` && "planning-highlight",
                          )}
                          style={{
                            // The dot center (px-2 pad + half of the 10px dot)
                            // lands on the grid point.
                            transform: labelLeft
                              ? "translate(calc(-100% + 14px), -50%)"
                              : "translate(-14px, -50%)",
                          }}
                          onPointerDown={startDrag(n)}
                          onPointerMove={moveDrag}
                          onPointerUp={endDrag(n, () => setExpandedId(n.id))}
                        >
                          <span
                            className={cn(
                              "h-2.5 w-2.5 shrink-0 rounded-full",
                              DOT_TONES[tone],
                            )}
                          />
                          {n.selected ? (
                            <Star className="h-3 w-3 shrink-0 fill-warning text-warning" />
                          ) : null}
                          <span className="truncate text-[11px] font-medium leading-4">
                            {n.title}
                          </span>
                        </div>
                      </div>
                    );
                  }

                  // Expanded: the full card. The grid point sits at a corner;
                  // flip which corner by quadrant so the card always opens
                  // toward the center and never clips at an edge.
                  const tx = p.x > 50 ? "-100%" : "0";
                  const ty = p.y > 50 ? "-100%" : "0";
                  return (
                    <div
                      key={n.id}
                      className="absolute z-10"
                      style={{
                        left: `${p.x}%`,
                        top: `${p.y}%`,
                        transform: `translate(${tx}, ${ty})`,
                      }}
                    >
                      {/* The "point": marks which card corner sits on the grid
                          coordinate (it flips with the quadrant anchor above). */}
                      <span
                        className="pointer-events-none absolute z-20 h-2.5 w-2.5 rounded-full border-2 border-background bg-power shadow-sm"
                        style={{
                          left: tx === "0" ? "0%" : "100%",
                          top: ty === "0" ? "0%" : "100%",
                          transform: "translate(-50%, -50%)",
                        }}
                      />
                      <NoteCard
                        note={n}
                        size="sm"
                        data-planning-hl={`note-${n.id}`}
                        className={cn(
                          "cursor-grab select-none active:cursor-grabbing",
                          drag?.id === n.id && "shadow-lg",
                          highlight === `note-${n.id}` && "planning-highlight",
                        )}
                        onPointerDown={startDrag(n)}
                        onPointerMove={moveDrag}
                        onPointerUp={endDrag(n, () => setDetailId(n.id))}
                      >
                        {n.vote_summary.revealed &&
                        n.vote_summary.avg_effort !== null &&
                        n.vote_summary.avg_impact !== null ? (
                          <div className="mt-1 flex items-center gap-2 text-[10px] font-semibold text-foreground/60">
                            <span>I {n.vote_summary.avg_impact}</span>
                            <span>E {n.vote_summary.avg_effort}</span>
                            {n.placement_override ? (
                              <span className="text-warning" title="Hand-placed — differs from scores">
                                ✋
                              </span>
                            ) : null}
                            <span>
                              {n.departments.length ? `${n.departments.length} dept` : ""}
                            </span>
                          </div>
                        ) : null}
                        <LineageLink
                          note={n}
                          workspace={workspace}
                          onNavigateBoard={onNavigateBoard}
                        />
                      </NoteCard>
                    </div>
                  );
                })}
              </div>
              <div className="flex justify-between px-1 pt-1 text-[10px] font-medium text-muted-foreground">
                <span>Low</span>
                <span className="text-xs font-semibold tracking-widest">Effort</span>
                <span>High</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Rescale confirm ── */}
      <Dialog open={rescaleOpen} onOpenChange={setRescaleOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Rescale the grid?</DialogTitle>
            <DialogDescription>
              Spreads the cluster out: the lowest and highest positions on each
              axis stretch to the edges, with the same ratio applied to every
              note — relative placement is preserved, just wider.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 text-sm text-muted-foreground">
            <p>
              Make sure <span className="font-medium text-foreground">everything is on the grid first</span>{" "}
              so the whole set shares one scaling perspective.
              {tray.length > 0 ? (
                <span className="mt-1 block font-medium text-warning">
                  {tray.length} initiative{tray.length === 1 ? " is" : "s are"} still in
                  the tray and won&apos;t be rescaled — placed later, they won&apos;t match
                  the new scale.
                </span>
              ) : (
                <span> All {placed.length} initiatives are placed — good to go.</span>
              )}
            </p>
            <p>
              Rescaled notes become hand-placed (✋); their recorded scores don&apos;t
              change. <span className="font-medium text-foreground">Revert scale</span>{" "}
              undoes this if it doesn&apos;t look right.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRescaleOpen(false)}>
              Cancel
            </Button>
            <Button
              disabled={rescale.isPending}
              onClick={() =>
                rescale.mutate(undefined, {
                  onSuccess: () => {
                    setRescaleOpen(false);
                    toast.success("Grid rescaled — Revert scale brings it back");
                  },
                  onError: (err) => toast.error(err.message),
                })
              }
            >
              {rescale.isPending ? "Rescaling…" : "Rescale"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <GridNoteDialog
        note={detailId ? (workspace.notes.find((n) => n.id === detailId) ?? null) : null}
        workspace={workspace}
        onOpenChange={(open) => !open && setDetailId(null)}
      />
      <InitiativeComposer
        workspace={workspace}
        open={composerOpen}
        onOpenChange={setComposerOpen}
      />
    </div>
  );
}

function InitiativeComposer({
  workspace,
  open,
  onOpenChange,
}: {
  workspace: Workspace;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const create = useCreateNote(workspace.session.id);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>New initiative</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <Field
            id="initiative-title"
            label="Initiative"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
          <Textarea
            placeholder="What would we actually do? (optional)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            disabled={!title.trim() || create.isPending}
            onClick={() =>
              create.mutate(
                {
                  board: "impact_effort",
                  title: title.trim(),
                  description: description.trim() || null,
                },
                {
                  onSuccess: () => {
                    onOpenChange(false);
                    setTitle("");
                    setDescription("");
                    toast.success("Added to the scoring tray");
                  },
                  onError: (err) => toast.error(err.message),
                },
              )
            }
          >
            {create.isPending ? "Adding…" : "Add to tray"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
